import { isUserEntity } from '@backstage/catalog-model';
import {
  DataProductEntityFilter,
  GroupEntityFilter,
  isApiEntity,
  isBusinessDomainEntity,
  isChapterEntity,
  isComponentEntity,
  isDataProductEntity,
  isDomainEntity,
  isGroupEntity,
  isOrganisationDsoEntity,
  isProductEntity,
  isResourceEntity,
  isSolutionEntity,
  isSquadEntity,
  isSystemEntity,
  isTribeEntity,
  OrganisationDsoEntityFilter,
  ProductEntityFilter,
  SolutionEntityFilter,
} from '@ipen-portal/ipen-entity-model';
import { patchTypedFilter } from '@ipen-portal/plugin-common';
import { BreadcrumbDefinitionBlueprint } from '@ipen-portal/plugin-navigation-react';

import {
  entitiesOfSameKindAndType,
  entitiesOfSameKindAndTypeAndSystem,
} from './siblings-extractors';

export const breadcrumbDefinitions = [
  BreadcrumbDefinitionBlueprint.make({
    name: 'Chapter',
    params: defineParams =>
      defineParams({
        filter: isChapterEntity,
        parent: {
          entityRefExtractor: entity => entity.spec.parent,
          fallbackKind: 'Domain',
          subPath: 'chapters',
          titleSuffix: 'Chapters',
        },
        siblingsExtractor: entitiesOfSameKindAndType,
      }),
  }),

  BreadcrumbDefinitionBlueprint.make({
    name: 'Squad',
    params: defineParams =>
      defineParams({
        filter: isSquadEntity,
        parent: {
          entityRefExtractor: entity => entity.spec.parent,
          fallbackKind: 'Domain',
          subPath: 'squads',
          titleSuffix: 'Squads',
        },
        siblingsExtractor: entitiesOfSameKindAndType,
      }),
  }),

  BreadcrumbDefinitionBlueprint.make({
    name: 'Tribe',
    params: defineParams => defineParams({ filter: isTribeEntity }),
  }),

  BreadcrumbDefinitionBlueprint.make({
    name: 'BusinessDomainEntity',
    params: defineParams =>
      defineParams({
        filter: isBusinessDomainEntity,
        root: { link: '/marketplace', title: 'Marketplace' },
        siblingsExtractor: entitiesOfSameKindAndType,
      }),
  }),

  BreadcrumbDefinitionBlueprint.make({
    name: 'Solution',
    params: defineParams =>
      defineParams({
        filter: isSolutionEntity,
        root: { link: '/marketplace', title: 'Marketplace' },
        siblingsExtractor: () => SolutionEntityFilter,
      }),
  }),

  BreadcrumbDefinitionBlueprint.make({
    name: 'Product',
    params: defineParams =>
      defineParams({
        filter: isProductEntity,
        parent: {
          entityRefExtractor: entity => entity.spec.domain,
          fallbackKind: 'Domain',
          subPath: 'products',
          titleSuffix: 'Products',
        },
        siblingsExtractor: entity =>
          patchTypedFilter(ProductEntityFilter, {
            'spec.domain': entity.spec.domain,
          }),
      }),
  }),

  BreadcrumbDefinitionBlueprint.make({
    name: 'DataProduct',
    params: defineParams =>
      defineParams({
        filter: isDataProductEntity,
        parent: {
          entityRefExtractor: entity => entity.spec.owner,
          fallbackKind: 'Domain',
          subPath: 'data-products',
          titleSuffix: 'Data Products',
        },
        siblingsExtractor: entity =>
          patchTypedFilter(DataProductEntityFilter, {
            'spec.owner': entity.spec.owner,
          }),
      }),
  }),

  BreadcrumbDefinitionBlueprint.make({
    name: 'OrganisationDso',
    params: defineParams =>
      defineParams({
        filter: isOrganisationDsoEntity,
        parent: {
          entityRefExtractor: entity => entity.spec.parent,
          fallbackKind: 'Group',
        },
        siblingsExtractor: () => OrganisationDsoEntityFilter,
      }),
  }),

  BreadcrumbDefinitionBlueprint.make({
    name: 'Api',
    params: defineParams =>
      defineParams({
        filter: isApiEntity,
        parent: {
          entityRefExtractor: entity => entity.spec.system,
          fallbackKind: 'System',
        },
        siblingsExtractor: entitiesOfSameKindAndTypeAndSystem,
      }),
  }),

  BreadcrumbDefinitionBlueprint.make({
    name: 'Component',
    params: defineParams =>
      defineParams({
        filter: isComponentEntity,
        parent: {
          entityRefExtractor: entity => entity.spec.system,
          fallbackKind: 'System',
        },
        siblingsExtractor: entitiesOfSameKindAndTypeAndSystem,
      }),
  }),

  BreadcrumbDefinitionBlueprint.make({
    name: 'Domain',
    params: defineParams =>
      defineParams({
        filter: isDomainEntity,
        parent: {
          entityRefExtractor: entity => entity.spec.subdomainOf,
          fallbackKind: 'Domain',
        },
        siblingsExtractor: entitiesOfSameKindAndType,
      }),
  }),

  BreadcrumbDefinitionBlueprint.make({
    name: 'Group',
    params: defineParams =>
      defineParams({
        filter: isGroupEntity,
        parent: {
          entityRefExtractor: entity => entity.spec.parent,
          fallbackKind: 'Group',
        },
        siblingsExtractor: e =>
          (e.spec.parent
            ? patchTypedFilter(GroupEntityFilter, {
                'spec.parent': e.spec.parent,
              })
            : null),
      }),
  }),

  BreadcrumbDefinitionBlueprint.make({
    name: 'Resource',
    params: defineParams =>
      defineParams({
        filter: isResourceEntity,
        parent: {
          entityRefExtractor: entity => entity.spec.system,
          fallbackKind: 'System',
        },
        siblingsExtractor: entitiesOfSameKindAndTypeAndSystem,
      }),
  }),

  BreadcrumbDefinitionBlueprint.make({
    name: 'System',
    params: defineParams =>
      defineParams({
        filter: isSystemEntity,
        parent: {
          entityRefExtractor: entity => entity.spec.domain,
          fallbackKind: 'Domain',
        },
        siblingsExtractor: entitiesOfSameKindAndType,
      }),
  }),

  BreadcrumbDefinitionBlueprint.make({
    name: 'User',
    params: defineParams => defineParams({ filter: isUserEntity }),
  }),
];
